import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import BulkActionsTable from "../../../modules/merchandising/components/tables/BulkActionsTable";
import { ProductCell } from "../../../modules/merchandising/components/cells/ProductCell";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { successToast, errorToast } from "../../../utils/toasts";
import { useProductLifecycleFilters } from "../../../modules/advertising/filters/productLifecycleFilters";
import useCustomTableFilters from "../../../hooks/useCustomTableFilters";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../utils/marketplaceConstants";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import { trellisPalette } from "../../custom/analytics/palettes";
import { useSimpleCan } from "../../../config/casl/casl";

export const ProductsTable = ({
  isLoading,
  productData,
  ExpandedContent,
  marketPlace,
  onBulkChange,
  bulkActionOptions,
  onSelection,
  renderProductStatusToggles,
  hideCompareValue = false,
  selectedProducts = [],
  cellContext = null,
  customColumns = null,
  showOffers = false,
  isSaving = false,
}) => {
  const TABLE_ID = "productsTable";
  const { t } = useTranslation(["pricingv2"]);
  const {
    INVENTORY_STATE_FILTER,
    SALES_STATE_FILTER,
    PRICING_PRODUCT_ENROLMENT_FILTER,
    buildProductVariantsFilter,
    buildCustomTagsFilter,
  } = useProductLifecycleFilters();
  const tableRef = useRef();
  const queryClient = useQueryClient();
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  const variants = useMemo(() => {
    const variants = {};
    if (productData) {
      for (const i in productData) {
        const s = productData[i];
        if (!s.variations) {
          continue;
        }
        try {
          const variations = s.variations;
          if (!variations) {
            continue;
          }
          Object.keys(variations).forEach((v) => {
            if (variants[v] === undefined) {
              variants[v] = [];
            }
            if (variants[v].indexOf(variations[v]) === -1) {
              variants[v].push(variations[v]);
            }
          });
        } catch {
          // Do Nothing
        }
      }
    }
    return variants;
  }, [productData]);

  const filterProps = useCustomTableFilters(TABLE_ID);

  const updatePricingProductHandler = useMutation({
    mutationFn: async (updateData) => {
      return await api.post(`/pricing/pricing_plan_products/pause`, updateData);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.pricingv2["Product Status Successfully Updated"]),
      });
      onSelection([]);
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.pricingv2["Failed to Update Product Status"]),
        error,
      });
    },
  });

  const updateProductStatus = (newStatus, productId, bulkAction = false) => {
    let productIds;

    if (bulkAction) {
      productIds = selectedProducts.join(",");
    } else {
      // Below is odd, but need to cast id as a string to avoid .split(',') failing in backend
      productIds = productId.toString();
    }

    updatePricingProductHandler.mutate({
      status: newStatus,
      marketplace: marketPlace.marketPlace,
      product_ids: productIds,
    });
  };

  const columnSpecs =
    customColumns ||
    [
      renderProductStatusToggles && {
        key: "status",
        compareKey: null,
        options: {
          editCell: true,
          hideFilter: false,
          toggleAction: (row, value) => {
            const { id: productId } = row;
            updateProductStatus(value, productId, false);
          },
          filterType: "select",
          selectOptions: [
            {
              value: "enabled",
              label: t(($) => $.pricingv2.Active),
            },
            {
              value: "paused",
              label: t(($) => $.pricingv2.Inactive),
            },
          ],
          format: "toggle",
          onValue: "enabled",
          offValue: "disabled",
          className: "justify-content-end",
          columnTitle: t(($) => $.pricingv2.Status),
          disabled: !userCanUpdatePricingPlans,
        },
      },
      {
        key: "product_price",
        compareKey: "price_delta",
        options: {
          checked: false,
          isNegative: true,
          noColor: true,
          metric: {
            id: "product_price",
            format: "currency",
            accessor: (d) => d?.product_price,
            name: t(($) => $.pricingv2["Current Price"]),
          },
        },
      },
      {
        key: "asp",
        compareKey: null,
        options: {
          checked: true,
          overrideCompare: true,
          overrideCompareAccessor: (d) => {
            return {
              total_units: d?.total_units,
              sales: d?.total_sales,
              total_units_prev: d?.total_units_prev,
              sales_prev: d?.total_sales_prev,
            };
          },
          overrideCompareFormatter: (v) => {
            const avgPrice = v.sales > 0 ? v.sales / v.total_units : 0;
            const preAvgPrice = v.total_units_prev > 0 ? v.sales_prev / v.total_units_prev : 0;
            const diff = (avgPrice - preAvgPrice) / preAvgPrice;
            let color = "#555";

            if (diff >= 0.03) {
              color = trellisPalette[12];
            } else if (diff <= -0.03) {
              color = trellisPalette[17];
            }
            return (
              <span style={{ color: "#555" }}>
                <span style={{ color: color }}>
                  {formatPercent(diff, {
                    addPlus: true,
                  })}
                </span>
              </span>
            );
          },
        },
      },
      {
        key: "suggested_price",
        compareKey: "price",
        options: {
          checked: false,
          isNegative: true,
          noColor: true,
          overrideCompare: true,
          overrideCompareAccessor: (d) => {
            if (d.suggested_price && d.product_price) {
              return d.suggested_price - d.product_price;
            }
            return 0;
          },
          overrideCompareFormatter: (v) =>
            formatCurrency(v, {
              marketPlace: marketPlace,
              addPlus: true,
            }),
          metric: {
            name: t(($) => $.pricingv2["Suggested {currency}"], {
              currency: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace],
            }),
          },
        },
      },
      {
        key: "sales",
        compareKey: "sales_percent",
      },
      { key: "units", compareKey: "units_percent", options: { checked: true } },
      {
        key: "profit",
        compareKey: "profit_delta",
        options: { checked: true },
      },
      {
        key: "total_conv_rate",
        compareKey: "total_conv_rate_delta",
        options: {
          checked: true,
          metric: {
            id: "total_conv_rate",
            name: t(($) => $.pricingv2["Conv/Rate"]),
            format: "percent",
          },
        },
      },
      {
        key: "bsr",
        compareKey: "bsr_percent",
        options: { checked: true },
      },
      {
        key: "page_views",
        compareKey: "page_views_percent",
        options: { checked: false },
      },
      {
        key: "min_price",
        compareKey: null,
        isStatic: true,
        options: {
          isStatic: true,
          editCell: true,
          format: "currency",
          minValue: 0,
          columnTitle: t(($) => $.pricingv2.Min),
          checked: true,
          disabled: !userCanUpdatePricingPlans,
        },
      },
      {
        key: "max_price",
        compareKey: null,
        options: {
          isStatic: true,
          editCell: true,
          format: "currency",
          minValue: 0,
          columnTitle: t(($) => $.pricingv2.Max),
          checked: true,
          disabled: !userCanUpdatePricingPlans,
        },
      },
      {
        key: "landed_cost",
        compareKey: null,
        options: {
          editCell: true,
          format: "currency",
          minValue: 0,
          columnTitle: t(($) => $.pricingv2.Landed),
          checked: false,
          toolTip: t(($) => $.pricingv2["The product's landed cost"]),
          disabled: !userCanUpdatePricingPlans,
        },
      },
      {
        key: "shipping_cost",
        compareKey: null,
        options: {
          editCell: true,
          format: "currency",
          minValue: 0,
          columnTitle: t(($) => $.pricingv2.Shipping),
          checked: false,
          toolTip: t(($) => $.pricingv2["The product's shipping cost"]),
          disabled: !userCanUpdatePricingPlans,
        },
      },
      {
        key: "profit_wo_adspend",
        compareKey: "profit_wo_adspend_delta",
        options: { checked: false },
      },
      {
        key: "cogs",
        compareKey: "cogs_percent",
        options: { checked: false },
      },
      {
        key: "margin",
        compareKey: "margin_delta",
        options: { checked: false },
      },
      {
        key: "margin_wo_adspend",
        compareKey: "margin_wo_adspend_delta",
        options: { checked: false },
      },
      {
        key: "ad_sales",
        compareKey: "ad_sales_percent",
        options: {
          checked: false,
        },
      },
      {
        key: "ad_spend",
        compareKey: "ad_spend_percent",
        options: {
          checked: false,
        },
      },
      {
        key: "ctr",
        compareKey: "ctr_delta",
        options: {
          checked: false,
        },
      },
      {
        key: "cpc",
        compareKey: "cpc_delta",
        options: {
          checked: false,
        },
      },
    ].filter((column) => !!column);

  const productVariantsFilters = useMemo(() => {
    return buildProductVariantsFilter(variants);
  }, [variants, buildProductVariantsFilter]);

  const customTags = useMemo(() => {
    return productData?.flatMap((product) => product.tags) ?? [];
  }, [productData]);

  const ADDITIONAL_FILTERS = [
    INVENTORY_STATE_FILTER,
    SALES_STATE_FILTER,
    PRICING_PRODUCT_ENROLMENT_FILTER,
    productVariantsFilters,
    buildCustomTagsFilter(customTags),
  ];

  return (
    <BulkActionsTable
      tableId={TABLE_ID}
      tableRef={tableRef}
      titleCol={{
        id: "products",
        accessor: "product_title",
        className: "text-start",
        width: 500,
        Header: t(($) => $.pricingv2.Products),
        isStatic: true,
        Cell: ({ value, row }) => {
          return (
            <ProductCell
              value={value}
              showOffers={showOffers}
              original={{
                ...row._original,
                id: row._original?.product_id,
                cellContext,
              }}
              showInventoryBadge={false}
            />
          );
        },
        filterMethod: () => {
          return;
        },
        Filter: () => null,
      }}
      columnSpecs={columnSpecs}
      data={productData}
      selectionKey="product_id"
      selected={selectedProducts}
      setSelected={onSelection}
      isExpandable={true}
      isSelectable={userCanUpdatePricingPlans}
      isLoading={isLoading}
      isSaving={isSaving}
      ExpandedContent={ExpandedContent}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      hideCompareValue={hideCompareValue}
      {...filterProps}
      additionalFilterOptions={ADDITIONAL_FILTERS}
      bulkActionOptions={bulkActionOptions}
      getSearchCriteria={(row) => {
        const { product_title, sku, asin, id, tags } = row;
        return `${product_title} ${sku} ${asin} ${id} ${tags?.join(" ")}`;
      }}
      minRows={0}
      tableRowDescription={t(($) => $.pricingv2.Products)}
      handleBulkChanges={onBulkChange || (() => {})}
    />
  );
};
